import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  Input,
  InputLabel,
  TextField,
} from "@mui/material";
import emailjs from "@emailjs/browser";
import { BorderColorButton } from "../BorderColorButton";
import { grey } from "@mui/material/colors";
import { FormEvent, useRef, useState } from "react";
import styleVariables from "../../variables.module.scss";

const {
  REACT_APP_EMAIL_SERVICE = "",
  REACT_APP_EMAIL_TEMPLATE = "",
  REACT_APP_EMAIL_PUBLIC_K = "",
} = process.env;

const EmailForm = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    debugger;
    emailjs
      .sendForm(
        REACT_APP_EMAIL_SERVICE,
        REACT_APP_EMAIL_TEMPLATE,
        formRef?.current as HTMLFormElement,
        {
          publicKey: REACT_APP_EMAIL_PUBLIC_K,
        }
      )
      .then(
        (result) => {
          console.log(result.text);
          alert("Message sent successfully!");
          setFormData({
            name: "",
            email: "",
            phone: "",
            address: "",
            subject: "",
            message: "",
          });
        },
        (error) => {
          console.error(error.text);
          alert("Error sending message. Please try again later.");
        }
      );
  };

  return (
    <Grid container direction="column" gap={2}>
      <Grid item>
        <Box
          sx={{
            typography: { xs: "h6", md: "h4" },
          }}
        >
          <Box
            sx={{ fontWeight: "bold" }}
            textAlign={{ xs: "center", md: "left" }}
          >
            REQUEST A FREE QUOTE
          </Box>
        </Box>
      </Grid>
      <Grid item>
        <form onSubmit={handleSubmit} ref={formRef}>
          <Grid container direction="column" gap={2}>
            <Grid item>
              <FormControl fullWidth error={!!errors.name}>
                <InputLabel required htmlFor="name-input">
                  Your Name
                </InputLabel>
                <Input
                  id="name-input"
                  aria-describedby="name-text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  sx={{
                    "& .MuiInputBase-input": {
                      backgroundColor: styleVariables.themeOrangeColor,
                      "&:-webkit-autofill": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                        WebkitTextFillColor: "#000",
                      },
                      "&:-webkit-autofill:hover": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                      "&:-webkit-autofill:focus": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                    },
                  }}
                />
                {errors.name && (
                  <Box
                    sx={{
                      color: "error.main",
                      fontSize: "0.75rem",
                      mt: 0.5,
                      fontWeight: "bold",
                    }}
                  >
                    {errors.name}
                  </Box>
                )}
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth error={!!errors.email}>
                <InputLabel required htmlFor="email-to-address">
                  Email address
                </InputLabel>
                <Input
                  id="email-to-address"
                  aria-describedby="email-to-address-text"
                  name="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  sx={{
                    "& .MuiInputBase-input": {
                      backgroundColor: styleVariables.themeOrangeColor,
                      "&:-webkit-autofill": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                        WebkitTextFillColor: "#000",
                      },
                      "&:-webkit-autofill:hover": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                      "&:-webkit-autofill:focus": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                    },
                  }}
                />
                {errors.email && (
                  <Box
                    sx={{
                      color: "error.main",
                      fontSize: "0.75rem",
                      mt: 0.5,
                      fontWeight: "bold",
                    }}
                  >
                    {errors.email}
                  </Box>
                )}
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth error={!!errors.phone}>
                <InputLabel required htmlFor="phone-number">
                  Phone Number
                </InputLabel>
                <Input
                  id="phone-number"
                  type="tel"
                  aria-describedby="phone-number"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      backgroundColor: styleVariables.themeOrangeColor,
                      "&:-webkit-autofill": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                        WebkitTextFillColor: "#000",
                      },
                      "&:-webkit-autofill:hover": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                      "&:-webkit-autofill:focus": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                    },
                  }}
                />
                {errors.phone && (
                  <Box
                    sx={{
                      color: "error.main",
                      fontSize: "0.75rem",
                      mt: 0.5,
                      fontWeight: "bold",
                    }}
                  >
                    {errors.phone}
                  </Box>
                )}
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth error={!!errors.address}>
                <InputLabel required htmlFor="personal-address">
                  Address
                </InputLabel>
                <Input
                  id="personal-address"
                  aria-describedby="personal-address"
                  name="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  sx={{
                    "& .MuiInputBase-input": {
                      backgroundColor: styleVariables.themeOrangeColor,
                      "&:-webkit-autofill": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                        WebkitTextFillColor: "#000",
                      },
                      "&:-webkit-autofill:hover": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                      "&:-webkit-autofill:focus": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                    },
                  }}
                />
                {errors.address && (
                  <Box
                    sx={{
                      color: "error.main",
                      fontSize: "0.75rem",
                      mt: 0.5,
                      fontWeight: "bold",
                    }}
                  >
                    {errors.address}
                  </Box>
                )}
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth error={!!errors.subject}>
                <InputLabel required htmlFor="email-subject">
                  Subject
                </InputLabel>
                <Input
                  id="email-subject"
                  aria-describedby="email-subject-text"
                  name="subject"
                  value={formData.subject}
                  onChange={(e) => handleInputChange("subject", e.target.value)}
                  sx={{
                    "& .MuiInputBase-input": {
                      backgroundColor: styleVariables.themeOrangeColor,
                      "&:-webkit-autofill": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                        WebkitTextFillColor: "#000",
                      },
                      "&:-webkit-autofill:hover": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                      "&:-webkit-autofill:focus": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                    },
                  }}
                />
                {errors.subject && (
                  <Box
                    sx={{
                      color: "error.main",
                      fontSize: "0.75rem",
                      mt: 0.5,
                      fontWeight: "bold",
                    }}
                  >
                    {errors.subject}
                  </Box>
                )}
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth error={!!errors.message}>
                <FormLabel required>How May We Help You?</FormLabel>
                <TextField
                  id="email-content"
                  aria-describedby="email-content-text"
                  minRows={5}
                  maxRows={5}
                  multiline
                  name="message"
                  value={formData.message}
                  onChange={(e) => handleInputChange("message", e.target.value)}
                  sx={{
                    "& .MuiInputBase-input": {
                      backgroundColor: styleVariables.themeOrangeColor,
                      "&:-webkit-autofill": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                        WebkitTextFillColor: "#000",
                      },
                      "&:-webkit-autofill:hover": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                      "&:-webkit-autofill:focus": {
                        WebkitBoxShadow: `0 0 0 1000px ${styleVariables.themeOrangeColor} inset`,
                      },
                    },
                  }}
                />
                {errors.message && (
                  <Box
                    sx={{
                      color: "error.main",
                      fontSize: "0.75rem",
                      mt: 0.5,
                      fontWeight: "bold",
                    }}
                  >
                    {errors.message}
                  </Box>
                )}
              </FormControl>
            </Grid>
            <Grid item>
              <BorderColorButton
                buttonBorderColor="black"
                buttonColor="black"
                buttonBorderColorHover={grey[900]}
                buttColorHover={grey[900]}
                type="submit"
                variant="outlined"
                size="large"
                sx={{ mt: 2 }}
              >
                Get A Quote
              </BorderColorButton>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

export default EmailForm;
